import React, { useState, useEffect } from 'react';
import { usersApi, factoriesApi } from '../services/api';
import Header from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Users as UsersIcon, Shield, ShoppingCart, Wrench, Factory, Plus, Key, Trash2, Loader2, Eye, EyeOff, UserCog, ShieldCheck, RotateCcw, Palette, Wallet } from 'lucide-react';
import { formatDate, cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { TABS, getDefaultTabKeysForRole, getEffectiveTabKeys } from '../lib/tabs';

const getRoleBadge = (role) => {
  switch (role) {
    case 'super_admin':
      return { icon: Shield, color: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' };
    case 'admin':
      return { icon: Shield, color: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400' };
    case 'production':
      return { icon: Wrench, color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' };
    case 'sales':
      return { icon: ShoppingCart, color: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' };
    case 'factory':
      return { icon: Factory, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' };
    case 'designer':
      return { icon: Palette, color: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400' };
    case 'finance':
      return { icon: Wallet, color: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-400' };
    default:
      return { icon: UsersIcon, color: 'bg-slate-100 text-slate-700' };
  }
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const [isPermsOpen, setIsPermsOpen] = useState(false);
  const [permsSelection, setPermsSelection] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Create user form
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sales',
    factory_id: ''
  });
  
  // Reset password form
  const [newPassword, setNewPassword] = useState('');
  // Change role form
  const [newRole, setNewRole] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchFactories();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchFactories = async () => {
    try {
      const response = await factoriesApi.getAll();
      setFactories(response.data);
    } catch (error) {
      console.error('Failed to load factories');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validate factory_id for factory role
    if (newUser.role === 'factory' && !newUser.factory_id) {
      toast.error('Please select a factory for factory user');
      return;
    }
    
    setSubmitting(true);
    try {
      await usersApi.create(newUser);
      toast.success('User created successfully');
      setIsCreateOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'sales', factory_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setSubmitting(true);
    try {
      await usersApi.resetPassword(selectedUser.id, newPassword);
      toast.success(`Password reset for ${selectedUser.name}`);
      setIsResetOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) return;
    
    try {
      await usersApi.delete(user.id);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setIsResetOpen(true);
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setIsRoleOpen(true);
  };

  const openPermsDialog = (user) => {
    setSelectedUser(user);
    setPermsSelection(getEffectiveTabKeys(user));
    setIsPermsOpen(true);
  };

  const togglePerm = (key) => {
    setPermsSelection(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSavePerms = async (resetToDefault = false) => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      // null = reset to role defaults; otherwise an explicit list
      const payload = resetToDefault ? null : [...permsSelection];
      await usersApi.setTabPermissions(selectedUser.id, payload);
      toast.success(resetToDefault ? `Reset ${selectedUser.name} to role defaults` : `Permissions updated for ${selectedUser.name}`);
      setIsPermsOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRole = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newRole) return;
    setSubmitting(true);
    try {
      await usersApi.changeRole(selectedUser.id, newRole);
      toast.success(`${selectedUser.name} is now ${newRole.replace('_', ' ')}`);
      setIsRoleOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header title="Users" />
        <div className="p-4 md:p-6">
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  const usersByRole = {
    super_admin: users.filter(u => u.role === 'super_admin'),
    admin: users.filter(u => u.role === 'admin'),
    production: users.filter(u => u.role === 'production'),
    sales: users.filter(u => u.role === 'sales')
  };

  return (
    <div className="min-h-screen" data-testid="users-page">
      <Header 
        title="Users" 
        onRefresh={fetchUsers}
        actions={
          <Button 
            onClick={() => setIsCreateOpen(true)}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="create-user-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Add User</span>
          </Button>
        }
      />

      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          {Object.entries(usersByRole).map(([role, roleUsers]) => {
            const { icon: Icon, color } = getRoleBadge(role);
            return (
              <Card key={role}>
                <CardContent className="p-3 md:p-6">
                  <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
                    <div className={cn("h-10 w-10 md:h-12 md:w-12 rounded-xl flex items-center justify-center", color.split(' ')[0])}>
                      <Icon className={cn("h-5 w-5 md:h-6 md:w-6", color.split(' ')[1])} />
                    </div>
                    <div className="text-center md:text-left">
                      <p className="text-xs md:text-sm text-slate-500 capitalize">{role.replace('_', ' ')}</p>
                      <p className="text-xl md:text-2xl font-bold">{roleUsers.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Users Table - Desktop */}
        <Card className="hidden md:block">
          <CardHeader>
            <CardTitle className="font-heading">All Users</CardTitle>
            <CardDescription>Manage system users and their access</CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  No users found
                </h3>
                <p className="text-slate-500 mb-4">
                  Create your first user to get started
                </p>
                <Button onClick={() => setIsCreateOpen(true)} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const { color } = getRoleBadge(user.role);
                    return (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className="bg-amber-100 text-amber-600 font-semibold">
                                {user.name?.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{user.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={cn(color, "capitalize")}>
                            {user.role.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isSuperAdmin && user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openPermsDialog(user)}
                                title="Tab Permissions"
                                data-testid={`perms-${user.id}`}
                              >
                                <ShieldCheck className={cn("h-4 w-4", Array.isArray(user.tab_permissions) ? "text-emerald-600" : "text-slate-400")} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openRoleDialog(user)}
                              title="Change Role"
                              data-testid={`change-role-${user.id}`}
                            >
                              <UserCog className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openResetDialog(user)}
                              title="Reset Password"
                              data-testid={`reset-password-${user.id}`}
                            >
                              <Key className="h-4 w-4 text-amber-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-500 hover:text-red-600"
                              title="Delete User"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Users Cards - Mobile */}
        <div className="md:hidden space-y-3">
          {users.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <UsersIcon className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-base font-semibold mb-2">No users found</h3>
                <p className="text-sm text-slate-500 mb-4">Create your first user</p>
                <Button onClick={() => setIsCreateOpen(true)} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </CardContent>
            </Card>
          ) : (
            users.map((user) => {
              const { color } = getRoleBadge(user.role);
              return (
                <Card key={user.id} data-testid={`user-card-${user.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-amber-100 text-amber-600 font-semibold">
                            {user.name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                      <Badge className={cn(color, "capitalize text-xs")}>
                        {user.role.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-neutral-800">
                      <span className="text-xs text-slate-500">Joined {formatDate(user.created_at)}</span>
                      <div className="flex gap-2">
                        {isSuperAdmin && user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openPermsDialog(user)}
                            title="Tab Permissions"
                          >
                            <ShieldCheck className={cn("h-4 w-4", Array.isArray(user.tab_permissions) ? "text-emerald-600" : "text-slate-400")} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openRoleDialog(user)}
                          title="Change Role"
                        >
                          <UserCog className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openResetDialog(user)}
                        >
                          <Key className="h-4 w-4 text-amber-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-heading">Create New User</DialogTitle>
            <DialogDescription>Add a new team member to the system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@company.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  className="pr-10"
                  data-testid="new-user-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales Team</SelectItem>
                  <SelectItem value="production">Production Team</SelectItem>
                  <SelectItem value="designer">Designer</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="factory">Factory User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Factory selection - only for factory role */}
            {newUser.role === 'factory' && (
              <div className="space-y-2">
                <Label htmlFor="factory">Assign to Factory *</Label>
                <Select 
                  value={newUser.factory_id} 
                  onValueChange={(v) => setNewUser({ ...newUser, factory_id: v })}
                >
                  <SelectTrigger data-testid="new-user-factory">
                    <SelectValue placeholder="Select factory..." />
                  </SelectTrigger>
                  <SelectContent>
                    {factories.map(factory => (
                      <SelectItem key={factory.id} value={factory.id}>
                        {factory.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-500 hover:bg-amber-600"
                disabled={submitting}
                data-testid="submit-create-user"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsResetOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-500 hover:bg-amber-600"
                disabled={submitting}
                data-testid="submit-reset-password"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={isRoleOpen} onOpenChange={setIsRoleOpen}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-heading">Change Role</DialogTitle>
            <DialogDescription>
              Update role for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangeRole} className="space-y-4">
            <div className="space-y-2">
              <Label>Current Role</Label>
              <p className="text-sm font-medium capitalize text-muted-foreground">{selectedUser?.role?.replace('_', ' ')}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role">New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger data-testid="change-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="production">Production Team</SelectItem>
                  <SelectItem value="designer">Designer</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="sales">Sales Team</SelectItem>
                  <SelectItem value="factory">Factory User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsRoleOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-500 hover:bg-amber-600"
                disabled={submitting || newRole === selectedUser?.role}
                data-testid="submit-change-role"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Tab Permissions Dialog */}
      <Dialog open={isPermsOpen} onOpenChange={setIsPermsOpen}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" /> Tab Permissions
            </DialogTitle>
            <DialogDescription>
              Choose which tabs <strong>{selectedUser?.name}</strong> can access.
              {!Array.isArray(selectedUser?.tab_permissions) && (
                <span className="block mt-1 text-xs">Currently using <span className="capitalize font-medium">{selectedUser?.role?.replace('_', ' ')}</span> role defaults.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1 -mr-1">
            {TABS.map(t => {
              const checked = permsSelection.includes(t.key);
              const isDefaultForRole = selectedUser ? getDefaultTabKeysForRole(selectedUser.role).includes(t.key) : false;
              return (
                <label
                  key={t.key}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                    checked ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-500/10" : "hover:bg-muted/40"
                  )}
                  data-testid={`perm-${t.key}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePerm(t.key)}
                    className="h-4 w-4 accent-emerald-600 flex-shrink-0"
                  />
                  <t.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.path}</p>
                  </div>
                  {isDefaultForRole && (
                    <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">role default</span>
                  )}
                </label>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSavePerms(true)}
              disabled={submitting}
              className="sm:mr-auto"
              data-testid="reset-perms-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset to role default
            </Button>
            <Button type="button" variant="outline" onClick={() => setIsPermsOpen(false)}>Cancel</Button>
            <Button
              type="button"
              onClick={() => handleSavePerms(false)}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="save-perms-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save permissions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
